import { formatEventTitle } from "@clockwise/web-commons/src/components/calendar/calendar-event/utils/formatEventTitle";
import { useReadCalendar } from "@clockwise/web-commons/src/util/CalendarContext";
import { formatDuration } from "@clockwise/web-commons/src/util/duration.util";
import { useOnWindowFocusChange } from "@clockwise/web-commons/src/util/react.util";
import { DateTime, ToHumanDurationOptions } from "luxon";
import { useEffect, useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import { usePlannerContext } from "../web-app-calendar/Context";
import { usePlannerEventCards } from "../web-app-calendar/hooks/usePlannerEventCards";
import { MENU_BAR_TRUNCATE_LONG_TITLES, SHOW_MENU_BAR_MEETING_TITLE } from "./Settings";
import { getMenuBarText } from "./util/util";

interface Props {
  userCalendarIds: string[];
}

export const MenuBarUpdater = ({ userCalendarIds }: Props) => {
  const {
    normalEvents: normalEventsNullable,
    getEventForCard,
    calendarDataLoading,
  } = usePlannerContext();

  const [now, setNow] = useState(DateTime.now());
  const [showMenuBarMeetingTitle] = useLocalStorage(SHOW_MENU_BAR_MEETING_TITLE, true);
  const [truncateLongTitles] = useLocalStorage(MENU_BAR_TRUNCATE_LONG_TITLES, true);

  // Set a 30 second interval to update a state value that will cause the component to re-render
  // this is to update the "ending in X minutes" header on the event preview
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(DateTime.now());
    }, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Upon change of normalEvents, get the next event that is happening now, or will happen next, and send it to the tray
  useEffect(() => {
    const normalEvents = normalEventsNullable ?? {};
    const eventsSelectedDay = normalEvents[DateTime.now().toISODate()];

    if (calendarDataLoading || !eventsSelectedDay) {
      return;
    }

    const nextEvent = eventsSelectedDay.find((event) => {
      const startTime = event.interval.start;
      const endTime = event.interval.end;
      return startTime > now || (startTime < now && endTime > now);
    });
    if (nextEvent && showMenuBarMeetingTitle) {
      const startTime = nextEvent.interval.start;
      const endTime = nextEvent.interval.end;
      const title = formatEventTitle(nextEvent.text, nextEvent.locked);

      const durationOpts: ToHumanDurationOptions = {
        unitDisplay: "narrow",
      };

      const getDurationString = (time: DateTime) =>
        formatDuration(time.diff(now, "minutes").minutes, durationOpts)
          .split(" ")
          .splice(0, 2) // If there is a third word, ditch it. Keep hours + min, or day + hour
          .join(" ");

      const suffix =
        startTime > now
          ? ` in ${getDurationString(startTime)}`
          : ` ${getDurationString(endTime)} left`;

      const menuBarTitle = getMenuBarText(title, truncateLongTitles ? 16 : 100) + suffix;
      const event = getEventForCard?.(nextEvent);

      window.tray?.nextEventTitle?.(menuBarTitle, event?.videoLink?.uri);
      return;

      // TODO, please read.
      // MenuBarUpdater does not get loaded when the user navigates to edit/create a link,
      // or to viewing a link. Therefore if they close the desktop app tray with this view open,
      // the menu bar will not update. We need to relocate MenuBarUpdater even higher, almost as a wrapper of
      // routes in routes modern. Idk if that is the solution, but something like that. Or, when we want to invest in
      // Desktop app big, move all this code into the electron app itself. Or, another idea, is to load a second headless (not visible) iframe of /app/mini
      // that loads just menu bar updater. That might be the way to go.
    }
    window.tray?.nextEventTitle?.("");
  }, [normalEventsNullable, now, showMenuBarMeetingTitle, truncateLongTitles]); // eslint-disable-line react-hooks/exhaustive-deps

  const { focusedDate } = useReadCalendar();

  // Start polling calendar events when in background
  const { startPolling, stopPolling } = usePlannerEventCards(focusedDate, userCalendarIds);

  // When window goes out of focus, startPolling
  useOnWindowFocusChange(
    () => null,
    () => setTimeout(() => startPolling(30 * 1000), 1000), // Call after the other stopPolling calls have finished by waiting 1 second
    stopPolling,
  );

  useEffect(() => {
    // If window is loaded while not in focus, start polling. This keeps events up-to-date if electron dies and gets reloaded in the background.
    if (!document.hasFocus()) {
      startPolling(30 * 1000);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};
